import { getApiBaseUrl } from '../lib/api';
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, BookOpen, List, MessageSquare } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Project } from '../types';
import TaskFrame from './TaskFrame';
import { getAuthToken } from '../lib/supabase';

interface TaskerProjectProps {
  project: Project;
  onBack: () => void;
}

type TaskerTab = 'instructions' | 'feedback' | 'tasks';

export const TaskerProject: React.FC<TaskerProjectProps> = ({ project, onBack }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [activeTab, setActiveTab] = useState<TaskerTab>('tasks');
  const [instructionsContent, setInstructionsContent] = useState<string | null>(null);
  const API_URL = getApiBaseUrl();

  // Tasking states moved to TaskFrame

  const ackKey = useMemo(() => `project:${project.id}:instructions_ack`, [project.id]);

  useEffect(() => {
    const hasAck = localStorage.getItem(ackKey) === '1';
    setAcknowledged(hasAck);
  }, [ackKey]);

  const handleAcknowledge = () => {
    localStorage.setItem(ackKey, '1');
    setAcknowledged(true);
  };

  // Load published enablement content for tasker view (fallback to spec)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAuthToken();
        if (!token) {
          setInstructionsContent(project.specContent || null);
          return;
        }
        const resp = await fetch(`${API_URL}/api/enablement/${project.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!resp.ok) {
          setInstructionsContent(project.specContent || null);
          return;
        }
        const data = await resp.json();
        const content: string | undefined = data?.enablement?.instructions?.content;
        if (!cancelled) setInstructionsContent(content || project.specContent || null);
      } catch {
        if (!cancelled) setInstructionsContent(project.specContent || null);
      }
    })();
    return () => { cancelled = true; };
  }, [project.id, project.specContent]);

  // Tasking UI moved to TaskFrame

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <button
          onClick={onBack}
          className="text-slate-600 hover:text-slate-900 flex items-center"
        >
          <ArrowLeft className="w-5 h-5 mr-2" /> Back
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('tasks')}
            className={`px-3 py-2 rounded-full text-sm font-medium ${activeTab === 'tasks' ? 'text-white' : 'bg-white ring-1 ring-zinc-200 text-zinc-700'}`}
            style={activeTab === 'tasks' ? { backgroundColor: 'var(--accent)' } : undefined}
          >
            <List className="w-4 h-4 inline mr-1" /> Tasks
          </button>
          <button
            onClick={() => setActiveTab('instructions')}
            className={`px-3 py-2 rounded-full text-sm font-medium ${activeTab === 'instructions' ? 'text-white' : 'bg-white ring-1 ring-zinc-200 text-zinc-700'}`}
            style={activeTab === 'instructions' ? { backgroundColor: 'var(--accent)' } : undefined}
          >
            <BookOpen className="w-4 h-4 inline mr-1" /> Instructions
          </button>
          <button
            onClick={() => setActiveTab('feedback')}
            className={`px-3 py-2 rounded-full text-sm font-medium ${activeTab === 'feedback' ? 'text-white' : 'bg-white ring-1 ring-zinc-200 text-zinc-700'}`}
            style={activeTab === 'feedback' ? { backgroundColor: 'var(--accent)' } : undefined}
          >
            <MessageSquare className="w-4 h-4 inline mr-1" /> Feedback
          </button>
        </div>
      </header>

      {!acknowledged ? (
        <div className="max-w-3xl mx-auto px-6 py-10">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Project Instructions</h1>
            <div
              className="prose prose-slate max-w-none
                prose-headings:font-semibold
                prose-h1:text-3xl prose-h1:mb-6 prose-h1:mt-8
                prose-h2:text-2xl prose-h2:mb-4 prose-h2:mt-6
                prose-h3:text-xl prose-h3:mb-3 prose-h3:mt-4
                prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:font-semibold prose-strong:text-slate-900
                prose-em:italic
                prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4
                prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4
                prose-li:mb-2 prose-li:text-slate-700
                prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                prose-pre:bg-slate-900 prose-pre:text-slate-100
                prose-blockquote:border-l-4 prose-blockquote:border-slate-300 prose-blockquote:pl-4 prose-blockquote:italic
                prose-hr:border-slate-200"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {instructionsContent || 'No instructions provided yet.'}
              </ReactMarkdown>
            </div>
            <div className="mt-8 text-right">
              <button
                onClick={handleAcknowledge}
                className="px-4 py-2 text-white rounded-full"
                style={{ backgroundColor: 'var(--accent)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
              >
                I have read the instructions
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-5xl mx-auto px-6 py-8">
          {activeTab === 'tasks' && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">Tasks</h2>
              <TaskFrame project={project} />
            </div>
          )}
          {activeTab === 'instructions' && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">Instructions</h2>
              <div
                className="prose prose-slate max-w-none
                  prose-headings:font-semibold
                  prose-h1:text-3xl prose-h1:mb-6 prose-h1:mt-8
                  prose-h2:text-2xl prose-h2:mb-4 prose-h2:mt-6
                  prose-h3:text-xl prose-h3:mb-3 prose-h3:mt-4
                  prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-strong:font-semibold prose-strong:text-slate-900
                  prose-em:italic
                  prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4
                  prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4
                  prose-li:mb-2 prose-li:text-slate-700
                  prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                  prose-pre:bg-slate-900 prose-pre:text-slate-100
                  prose-blockquote:border-l-4 prose-blockquote:border-slate-300 prose-blockquote:pl-4 prose-blockquote:italic
                  prose-hr:border-slate-200"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {instructionsContent || 'No instructions provided yet.'}
                </ReactMarkdown>
              </div>
            </div>
          )}
          {activeTab === 'feedback' && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">Feedback</h2>
              <p className="text-slate-600">Coming soon.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


